import React, { useMemo } from "react";
import { BarChart as MuiBarChart } from "@mui/x-charts/BarChart";
import type { ChartsTextStyle } from "@mui/x-charts/ChartsText";
import styles from "./Charts.module.scss";
import type { ChartDatum } from "./LineAreaChart";
import { formatCompactNumber, formatFullNumber } from "./chartUtils";

type BarChartProps = {
  data: ChartDatum[];
  barColor: string;
  title?: string;
};

const chartHeight = 320;
const axisLabelStyle: ChartsTextStyle = {
  fill: "var(--muted)",
  fontSize: 12,
  fontWeight: 500,
};

const chartSx = {
  "--Charts-grid-color": "rgba(15, 23, 42, 0.08)",
  "--Charts-axis-lineColor": "rgba(15, 23, 42, 0.12)",
  "--Charts-axis-tickColor": "rgba(15, 23, 42, 0.38)",
  "--Charts-tooltip-bg": "var(--surface, #fff)",
  "--Charts-tooltip-text": "var(--text, #0f172a)",
} as const;

function BarChart({ data, barColor, title }: BarChartProps) {
  const ariaLabel = title ?? "Bar chart";
  const labels = useMemo(() => data.map((datum) => datum.label), [data]);
  const values = useMemo(() => data.map((datum) => datum.value), [data]);

  return (
    <figure className={styles.chartFigure}>
      {title && <figcaption className={styles.title}>{title}</figcaption>}
      <div className={styles.chart}>
        <MuiBarChart
          aria-label={ariaLabel}
          height={chartHeight}
          xAxis={[
            {
              id: "months",
              data: labels,
              scaleType: "band",
              tickLabelStyle: axisLabelStyle,
            },
          ]}
          yAxis={[
            {
              id: "values",
              tickLabelStyle: axisLabelStyle,
              valueFormatter: (value: number | null) =>
                formatCompactNumber(
                  typeof value === "number" ? value : Number(value)
                ),
            },
          ]}
          margin={{ top: 20, right: 16, bottom: 32, left: 32 }}
          series={[
            {
              id: title ?? "bar-series",
              data: values,
              color: barColor,
              label: title,
              valueFormatter: (value: number | null) => formatFullNumber(value),
            },
          ]}
          grid={{ horizontal: true, vertical: false }}
          hideLegend
          slotProps={{
            bar: { rx: 6, ry: 6 },
          }}
          sx={chartSx}
        />
      </div>
    </figure>
  );
}

export default BarChart;
